// ============================================================
//  ALTIMO Medical Center — Laboratorio.jsx  (v2.1)
//  Fix: lab externo permite escribir nombre de examen libremente
// ============================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../apiClient';
import { useConfirmar } from './ModalConfirmar';

const TIPOS_EXAMEN = [
  'Hemograma completo','Química sanguínea','Glucemia','Perfil lipídico',
  'Perfil renal','Perfil hepático','Examen general de orina','Coagulación (TP/TPT)',
  'Electrocardiograma','Radiografía','Ultrasonido','Tomografía',
  'Resonancia magnética','Cultivo y sensibilidad','Prueba COVID-19',
  'Prueba de embarazo','Grupo sanguíneo','Serología','Otro',
];

const TIPO_ICONO = {
  pdf:{ bg:'#f8d7da',color:'#721c24',label:'PDF'},docx:{bg:'#cce5ff',color:'#004085',label:'WORD'},
  doc:{bg:'#cce5ff',color:'#004085',label:'WORD'},jpg:{bg:'#d4edda',color:'#155724',label:'IMG'},
  jpeg:{bg:'#d4edda',color:'#155724',label:'IMG'},png:{bg:'#d4edda',color:'#155724',label:'IMG'},
  tiff:{bg:'#d4edda',color:'#155724',label:'IMG'},tif:{bg:'#d4edda',color:'#155724',label:'IMG'},
};
const EXTS_OK = ['pdf','doc','docx','jpg','jpeg','png','tiff','tif'];
const MAX_MB  = 20;

const S = {
  pagina:{ background:'#f0f4f8', minHeight:'100vh', padding:'20px', fontFamily:'Segoe UI, sans-serif' },
  card:  { background:'#fff', borderRadius:'10px', border:'1px solid #dde3ea', padding:'18px', marginBottom:'14px' },
  titulo:{ fontSize:'12px', fontWeight:'700', color:'#1B2A5E', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'14px', paddingBottom:'10px', borderBottom:'2px solid #e8f0fb', display:'flex', justifyContent:'space-between', alignItems:'center' },
  label: { display:'block', fontSize:'11px', fontWeight:'600', color:'#6c757d', marginBottom:'4px', textTransform:'uppercase', letterSpacing:'.04em' },
  input: { width:'100%', fontSize:'13px', padding:'8px 10px', border:'1px solid #c0ccda', borderRadius:'7px', background:'#fff', color:'#333', outline:'none', boxSizing:'border-box' },
  campo: { marginBottom:'12px' },
  btn:   (c) => ({ background:c, color:'#fff', border:'none', borderRadius:'7px', padding:'8px 16px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }),
  btnS:  { background:'#fff', color:'#495057', border:'1px solid #c0ccda', borderRadius:'7px', padding:'7px 12px', fontSize:'12px', cursor:'pointer' },
  alerta:(t) => ({
    padding:'10px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:'500', marginBottom:'12px',
    background:t==='ok'?'#d4edda':t==='err'?'#f8d7da':'#fff3cd',
    color:t==='ok'?'#155724':t==='err'?'#721c24':'#856404',
    border:`1px solid ${t==='ok'?'#c3e6cb':t==='err'?'#f5c6cb':'#ffc107'}`,
  }),
  fila2:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' },
  resultado:{ border:'1px solid #dde3ea', borderRadius:'8px', marginTop:'4px', maxHeight:'180px', overflowY:'auto', background:'#fff', boxShadow:'0 4px 12px rgba(0,0,0,.08)', position:'relative', zIndex:10 },
  resultadoItem:{ padding:'10px 14px', cursor:'pointer', fontSize:'13px', borderBottom:'1px solid #f0f4f8' },
  pacienteBox:  { background:'#e8f0fb', borderRadius:'8px', padding:'12px 16px', fontSize:'13px', display:'flex', justifyContent:'space-between', alignItems:'center' },
  seccionExterno:  { background:'#fff8e1', border:'2px solid #ffe082', borderRadius:'10px', padding:'14px', marginBottom:'12px' },
  seccionTarifario:{ background:'#e8f0fb', border:'1px solid #c8ddf7', borderRadius:'10px', padding:'14px', marginBottom:'12px' },
  montoManualBox:  { background:'#fff3e0', border:'2px solid #ff9800', borderRadius:'10px', padding:'16px', marginTop:'10px' },
  totalBadge:{ display:'inline-flex', alignItems:'center', gap:'8px', padding:'10px 16px', borderRadius:'8px', background:'#1B2A5E', color:'#fff', fontSize:'15px', fontWeight:'800', marginTop:'10px' },
};

function FileIcono({ ext }) {
  const cfg = TIPO_ICONO[ext?.toLowerCase()] || { bg:'#e2e3e5',color:'#383d41',label:'FILE' };
  return (
    <div style={{ width:'38px',height:'44px',background:cfg.bg,borderRadius:'6px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
      <span style={{ fontSize:'9px',fontWeight:'800',color:cfg.color,letterSpacing:'.03em' }}>{cfg.label}</span>
    </div>
  );
}

function RegistroExamenes({ usuario }) {
  const [busqueda,    setBusqueda]    = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [admisionId,  setAdmisionId]  = useState(null);
  const [pacienteId,  setPacienteId]  = useState(null);
  const [pacienteInfo,setPacienteInfo]= useState(null);

  // ── Modo Paciente Particular ─────────────────────────────
  const [modoParticular,      setModoParticular]      = useState(false);
  const [partNombre,          setPartNombre]          = useState('');
  const [partApellido,        setPartApellido]        = useState('');
  const [partDui,             setPartDui]             = useState('');
  const [partTelefono,        setPartTelefono]        = useState('');
  const [partFechaNac,        setPartFechaNac]        = useState('');
  const [registrandoPart,     setRegistrandoPart]     = useState(false);
  const [admisionParticular,  setAdmisionParticular]  = useState(null); // { id, pacienteNombre }
  const [mostrarRecibo,       setMostrarRecibo]       = useState(false);
  const { confirmar, ModalConfirmarUI } = useConfirmar();

  // Tipo de examen — select para interno, texto libre para externo
  const [tipoExamen,      setTipoExamen]      = useState('Hemograma completo');
  const [tipoExamenLibre, setTipoExamenLibre] = useState('');
  const [notas,           setNotas]           = useState('');

  // Lab externo
  const [esExterno,        setEsExterno]        = useState(false);
  const [labExterno,       setLabExterno]       = useState('');
  const [modoCargoExterno, setModoCargoExterno] = useState('tarifario');
  const [montoManual,      setMontoManual]      = useState('');

  // Tarifario
  const [cobrarCuenta,     setCobrarCuenta]     = useState(true);
  const [tarifaId,         setTarifaId]         = useState('');
  const [tarifas,          setTarifas]          = useState([]);
  const [tarifaFiltro,     setTarifaFiltro]     = useState('');
  const [tarifasFiltradas, setTarifasFiltradas] = useState([]);

  // Archivos
  const [archivosQueue, setArchivosQueue] = useState([]);
  const [dragOver,      setDragOver]      = useState(false);
  const [subiendo,      setSubiendo]      = useState(false);
  const [resultados,    setResultados]    = useState([]);
  const [cargando,      setCargando]      = useState(false);
  const [mensaje,       setMensaje]       = useState(null);
  const inputRef = useRef();

  // Nombre final del examen
  const nombreExamen = esExterno
    ? (tipoExamenLibre.trim() || 'Examen externo')
    : tipoExamen;

  useEffect(() => {
    api.tarifario.listar().then(r => {
      if (r.ok) { setTarifas(r.data); setTarifasFiltradas(r.data.slice(0,30)); }
    });
  }, []);

  useEffect(() => {
    if (!tarifaFiltro) { setTarifasFiltradas(tarifas.slice(0,30)); return; }
    setTarifasFiltradas(
      tarifas.filter(t =>
        t.nombre.toLowerCase().includes(tarifaFiltro.toLowerCase()) ||
        (t.codigo && t.codigo.toLowerCase().includes(tarifaFiltro.toLowerCase()))
      ).slice(0,20)
    );
  }, [tarifaFiltro, tarifas]);

  useEffect(() => {
    if (!cobrarCuenta || esExterno) return;
    const match = tarifas.find(t => t.nombre.toLowerCase().includes(tipoExamen.toLowerCase().slice(0,10)));
    if (match) { setTarifaId(String(match.id)); setTarifaFiltro(match.nombre); }
    else        { setTarifaId(''); }
  }, [tipoExamen, tarifas, cobrarCuenta, esExterno]);

  useEffect(() => {
    if (esExterno) { setModoCargoExterno('tarifario'); setMontoManual(''); setTipoExamenLibre(''); }
  }, [esExterno]);

  useEffect(() => {
    if (busqueda.length < 2) { setSugerencias([]); return; }
    const t = setTimeout(async () => {
      const r = await api.pacientes.buscar(busqueda);
      if (r.ok) setSugerencias(r.data);
    }, 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const cargarResultados = useCallback(async () => {
    if (!admisionId) return;
    setCargando(true);
    const r = await api.lab.listar(admisionId);
    if (r.ok) setResultados(r.data);
    setCargando(false);
  }, [admisionId]);

  useEffect(() => { cargarResultados(); }, [cargarResultados]);

  const mostrarMsg = (tipo, texto) => { setMensaje({ tipo, texto }); setTimeout(() => setMensaje(null), 6000); };

  const seleccionarPaciente = async (p) => {
    setBusqueda(`${p.nombre} ${p.apellido}`); setSugerencias([]); setPacienteId(p.id);
    const rH = await api.expedientes.historial(p.id);
    if (rH.ok && rH.data.length > 0) {
      const activa = rH.data.find(a => a.estado === 'Activa') || rH.data[0];
      setAdmisionId(activa.id);
      setPacienteInfo({ nombre:`${p.nombre} ${p.apellido}`, dui:p.dui||'—', admision:activa.id, tipo:activa.tipo_ingreso, estado:activa.estado });
    } else {
      mostrarMsg('warn','Este paciente no tiene admisiones registradas.');
      setAdmisionId(null); setPacienteInfo(null);
    }
  };

  const limpiarPaciente = () => {
    setBusqueda(''); setSugerencias([]);
    setAdmisionId(null); setPacienteId(null); setPacienteInfo(null);
    setResultados([]); setArchivosQueue([]);
  };

  // ── Registrar Paciente Particular ──────────────────────────
  const registrarParticular = async () => {
    const nombre   = partNombre.trim().toUpperCase();
    const apellido = partApellido.trim().toUpperCase();
    const telefono = partTelefono.trim();
    const dui      = partDui.trim() || null;

    if (!nombre)   { mostrarMsg('err','El nombre es obligatorio.'); return; }
    if (!apellido) { mostrarMsg('err','El apellido es obligatorio.'); return; }
    if (!telefono) { mostrarMsg('err','El teléfono es obligatorio.'); return; }

    setRegistrandoPart(true);
    try {
      // 1. Buscar si el paciente ya existe por DUI o nombre
      let paciente = null;
      if (dui) {
        const rBusc = await api.pacientes.buscar(dui);
        if (rBusc.ok && rBusc.data?.length > 0) {
          const match = rBusc.data.find(p => p.dui === dui);
          if (match) paciente = match;
        }
      }

      // 2. Si no existe, crear el paciente
      if (!paciente) {
        const rCrear = await api.pacientes.crear({
          nombre, apellido,
          dui:       dui || null,
          telefono:  telefono || null,
          fecha_nac: partFechaNac || null,
          sexo: null, email: null,
          direccion: null, nacionalidad: 'Salvadoreño/a',
          grupo_sanguineo: null,
          familiar_nombre: null, familiar_parentesco: null,
          familiar_telefono: null, familiar_direccion: null,
        });
        if (!rCrear.ok) throw new Error(rCrear.error);
        const newId = Number(rCrear.data?.lastInsertRowid ?? rCrear.data?.id);
        const rGet  = await api.pacientes.obtener(newId);
        if (!rGet.ok) throw new Error('No se pudo obtener el paciente creado.');
        paciente = rGet.data;
      }

      // 3. Crear admisión tipo "Examenes" automáticamente
      const subtipoExamen = tipoExamen === 'Otro'
        ? (tipoExamenLibre.trim() || 'Examen de laboratorio')
        : tipoExamen;

      const rAdm = await api.admisiones.crear({
        paciente_id:   paciente.id,
        habitacion_id: null,
        clinica_id:    null,
        medico_id:     null,
        tipo_ingreso:  'Examenes',
        subtipo:       subtipoExamen,
        motivo:        'Paciente particular — servicio de laboratorio',
        usuario_id:    usuario?.id || null,
        fecha_cita:    null, hora_cita: null,
        es_remitido:   0, dr_remite: null,
        especialidad_remite: null, institucion_remite: null, motivo_remision: null,
        tiene_reporte_policial: 0, numero_reporte: null,
        delegacion_policial: null, direccion_atencion: null,
      });
      if (!rAdm.ok) throw new Error(rAdm.error);

      const admId = Number(rAdm.data?.lastInsertRowid ?? rAdm.data?.id);
      const nombreCompleto = `${paciente.nombre} ${paciente.apellido}`;

      // 4. Actualizar estado del módulo igual que si fuera un paciente admitido
      setPacienteId(paciente.id);
      setAdmisionId(admId);
      setPacienteInfo({
        nombre:   nombreCompleto,
        dui:      paciente.dui || '—',
        admision: admId,
        tipo:     'Exámenes (Particular)',
        estado:   'Activa',
      });
      setAdmisionParticular({ id: admId, pacienteNombre: nombreCompleto });

      mostrarMsg('ok', `✓ Paciente particular registrado. Admisión #${admId} creada.`);

    } catch(e) {
      mostrarMsg('err', e.message);
    } finally {
      setRegistrandoPart(false);
    }
  };

  const limpiarParticular = () => {
    setPartNombre(''); setPartApellido(''); setPartDui('');
    setPartTelefono(''); setPartFechaNac('');
    setAdmisionParticular(null); setMostrarRecibo(false);
    limpiarPaciente();
  };

  // ── Generar recibo directo para particular ──────────────────
  const generarReciboParticular = () => {
    if (!admisionParticular || !montoACobrar) {
      mostrarMsg('warn', 'Primero guarda el examen con un cargo asignado.'); return;
    }
    const fecha = new Date().toLocaleDateString('es-SV');
    const hora  = new Date().toLocaleTimeString('es-SV');
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
<style>
  body{font-family:'Segoe UI',sans-serif;color:#222;margin:0;padding:32px;font-size:13px}
  .cab{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px}
  .logo{font-size:26px;font-weight:800;color:#1B2A5E}.sub{font-size:10px;letter-spacing:2px;color:#1D9E75}
  .recibo-num{text-align:right;font-size:13px;color:#6c757d}
  h2{color:#1B2A5E;font-size:14px;margin:16px 0 8px;border-bottom:2px solid #e8f0fb;padding-bottom:4px}
  .fila{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #f0f4f8;font-size:13px}
  .total{display:flex;justify-content:space-between;padding:12px 0;font-size:18px;font-weight:800;color:#1B2A5E;border-top:2px solid #1B2A5E;margin-top:8px}
  .badge{background:#e8f0fb;border-radius:6px;padding:3px 10px;font-size:11px;font-weight:700;color:#1B2A5E}
  .firma{margin-top:40px;display:flex;gap:60px}
  .fi{text-align:center;flex:1;border-top:1px solid #333;padding-top:6px;font-size:11px}
  .footer{margin-top:30px;text-align:center;font-size:10px;color:#adb5bd;border-top:1px solid #eee;padding-top:12px}
  @media print{body{padding:12px}}
</style></head><body>
<div class="cab">
  <div><div class="logo">ALTIMO</div><div class="sub">MEDICAL CENTER</div>
  <div style="font-size:11px;color:#6c757d;margin-top:4px">Laboratorio Clínico</div></div>
  <div class="recibo-num">
    <div style="font-size:15px;font-weight:700;color:#1B2A5E">RECIBO DE PAGO</div>
    <div>Admisión #${admisionParticular.id}</div>
    <div>${fecha} ${hora}</div>
    <div style="margin-top:4px"><span class="badge">PACIENTE PARTICULAR</span></div>
  </div>
</div>
<hr style="border:none;border-top:2px solid #1B2A5E;margin-bottom:16px"/>
<h2>Datos del paciente</h2>
<div class="fila"><span>Nombre</span><strong>${admisionParticular.pacienteNombre}</strong></div>
<div class="fila"><span>DUI</span><span>${partDui || '—'}</span></div>
<div class="fila"><span>Teléfono</span><span>${partTelefono || '—'}</span></div>
<h2>Detalle del servicio</h2>
<div class="fila"><span>Tipo de examen</span><strong>${nombreExamen}</strong></div>
${esExterno ? `<div class="fila"><span>Laboratorio</span><span>${labExterno || 'Externo'}</span></div>` : ''}
<div class="fila"><span>Procesado por</span><span>ALTIMO Medical Center</span></div>
<div class="fila"><span>Atendido por</span><span>${usuario?.nombre || '—'}</span></div>
<h2>Resumen de pago</h2>
<div class="fila"><span>Servicio</span><span>$${montoACobrar.toFixed(2)}</span></div>
<div class="fila"><span>IVA (incluido)</span><span>$${(montoACobrar * 0.13 / 1.13).toFixed(2)}</span></div>
<div class="total"><span>TOTAL</span><span>$${montoACobrar.toFixed(2)}</span></div>
<div class="firma">
  <div class="fi">Firma del paciente</div>
  <div class="fi">Firma / Sello ALTIMO</div>
</div>
<div class="footer">ALTIMO Medical Center · Gracias por preferirnos<br/>Este comprobante es válido como recibo de pago por servicio de laboratorio.</div>
<script>window.onload=()=>window.print()</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=700,height=650');
    w.document.write(html); w.document.close();
  };

  const leerBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(file);
  });

  const validarArchivo = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!EXTS_OK.includes(ext)) return `Tipo no permitido: .${ext}`;
    if (file.size > MAX_MB * 1024 * 1024) return `Supera ${MAX_MB} MB`;
    return null;
  };

  const agregarAQueue = (files) => {
    const nuevos = [];
    for (const f of files) {
      const error = validarArchivo(f);
      if (error) { mostrarMsg('err', `${f.name}: ${error}`); continue; }
      nuevos.push(f);
    }
    setArchivosQueue(q => [...q, ...nuevos]);
  };

  const tarifaSeleccionada = tarifas.find(t => String(t.id) === tarifaId);

  const montoACobrar = (() => {
    if (!cobrarCuenta) return null;
    if (esExterno) {
      if (modoCargoExterno === 'manual') { const v = parseFloat(montoManual); return isNaN(v)||v<=0 ? null : v; }
      if (modoCargoExterno === 'tarifario') return tarifaSeleccionada ? Number(tarifaSeleccionada.precio) : null;
      return null;
    }
    return tarifaSeleccionada ? Number(tarifaSeleccionada.precio) : null;
  })();

  const subirTodo = async () => {
    if (!admisionId||!pacienteId) { mostrarMsg('err','Selecciona un paciente primero.'); return; }
    if (archivosQueue.length===0) { mostrarMsg('warn','Agrega al menos un archivo.'); return; }
    if (esExterno && modoCargoExterno==='manual') {
      const v = parseFloat(montoManual);
      if (isNaN(v)||v<=0) { mostrarMsg('err','Ingresa un monto válido.'); return; }
    }
    if (cobrarCuenta && !montoACobrar && modoCargoExterno!=='sin-cargo') {
      if (!(await confirmar({
        texto: 'Sin tarifa/monto. El examen se guardará SIN cargo.\n\n¿Continuar?',
        tipo: 'advertencia',
        textoConfirmar: 'Continuar sin cargo',
      }))) return;
    }

    setSubiendo(true); let exitos = 0;
    for (const file of archivosQueue) {
      try {
        const ext = '.'+file.name.split('.').pop().toLowerCase();
        const fileData = await leerBase64(file);
        const notaFull = [notas, esExterno ? `Lab externo: ${labExterno||'N/E'}` : 'Lab interno ALTIMO',
          esExterno && modoCargoExterno==='manual' ? `Monto externo: $${parseFloat(montoManual).toFixed(2)}` : null,
        ].filter(Boolean).join(' | ');
        const r = await api.lab.subir({
          admision_id:admisionId, paciente_id:pacienteId, tipo_examen:nombreExamen,
          notas:notaFull, usuario_id:usuario?.id, fileData, fileExt:ext,
        });
        if (r.ok) exitos++; else mostrarMsg('err',`Error subiendo ${file.name}: ${r.error}`);
      } catch(e) { mostrarMsg('err',`Error: ${e.message}`); }
    }

    let cargado = false;
    if (exitos>0 && cobrarCuenta && montoACobrar) {
      try {
        let descripcion='', referencia_id=null;
        if (esExterno && modoCargoExterno==='manual')
          descripcion = `${nombreExamen} — Lab externo: ${labExterno||'N/E'} (monto manual)`;
        else if (esExterno && modoCargoExterno==='tarifario' && tarifaSeleccionada)
          { descripcion=`${tarifaSeleccionada.nombre} (Lab externo: ${labExterno||'N/E'})`; referencia_id=Number(tarifaSeleccionada.id); }
        else if (!esExterno && tarifaSeleccionada)
          { descripcion=tarifaSeleccionada.nombre; referencia_id=Number(tarifaSeleccionada.id); }
        const rc = await api.cuenta.agregarItem({
          admision_id:Number(admisionId), tipo:'Examen', referencia_id, descripcion,
          cantidad:1, precio_unitario:montoACobrar, usuario_id:usuario?.id||null,
        });
        if (rc.ok) cargado=true; else mostrarMsg('err',`Archivos guardados pero el cargo falló: ${rc.error}`);
      } catch(e) { mostrarMsg('err',`Cargo falló: ${e.message}`); }
    }

    setSubiendo(false); setArchivosQueue([]); setNotas('');
    if (esExterno && modoCargoExterno==='manual') setMontoManual('');
    if (exitos>0) {
      const msgCargo = cobrarCuenta ? (cargado ? ` · Cargo $${montoACobrar.toFixed(2)} agregado ✓` : ' · ⚠ Sin cargo') : '';
      mostrarMsg('ok',`${exitos} archivo(s) guardado(s).${msgCargo}`);
      cargarResultados();
    }
  };

  const porTipo = resultados.reduce((acc,r) => {
    if (!acc[r.tipo_examen]) acc[r.tipo_examen]=[];
    acc[r.tipo_examen].push(r); return acc;
  }, {});

  return (
    <div style={S.pagina}>
      {ModalConfirmarUI}
      {mensaje && <div style={S.alerta(mensaje.tipo)}>{mensaje.texto}</div>}

      {/* PASO 1: Paciente */}
      <div style={S.card}>
        <div style={S.titulo}><span>Paso 1 — Paciente / Admisión</span></div>

        {/* ── Selector de modo ── */}
        {!pacienteInfo && (
          <div style={{ display:'flex', gap:'8px', marginBottom:'16px' }}>
            {[
              { id:false, label:'🏥 Paciente ALTIMO', desc:'Buscar por nombre o DUI' },
              { id:true,  label:'👤 Paciente Particular', desc:'Solo viene por un examen' },
            ].map(m => (
              <button key={String(m.id)}
                onClick={() => { setModoParticular(m.id); limpiarParticular(); }}
                style={{ flex:1, padding:'10px 14px', borderRadius:'9px', cursor:'pointer', border:'none',
                  background: modoParticular === m.id ? '#1B2A5E' : '#e8f0fb',
                  color: modoParticular === m.id ? '#fff' : '#1B2A5E',
                  fontWeight:'700', fontSize:'13px', textAlign:'left', lineHeight:'1.4' }}>
                {m.label}
                <div style={{ fontSize:'10px', opacity:.7, fontWeight:'400', marginTop:'2px' }}>{m.desc}</div>
              </button>
            ))}
          </div>
        )}

        {/* ── Modo ALTIMO: buscar paciente ── */}
        {!modoParticular && !pacienteInfo && (
          <div style={S.campo}>
            <label style={S.label}>Buscar paciente por nombre o DUI</label>
            <div style={{ position:'relative' }}>
              <input style={S.input} value={busqueda} onChange={e=>setBusqueda(e.target.value)} placeholder="Escribe para buscar..." />
              {sugerencias.length>0 && (
                <div style={S.resultado}>
                  {sugerencias.map(p=>(
                    <div key={p.id} style={S.resultadoItem}
                      onMouseEnter={e=>e.currentTarget.style.background='#f0f4f8'}
                      onMouseLeave={e=>e.currentTarget.style.background='#fff'}
                      onClick={()=>seleccionarPaciente(p)}>
                      <strong>{p.nombre} {p.apellido}</strong>
                      <span style={{ color:'#6c757d',fontSize:'12px',marginLeft:'10px' }}>DUI: {p.dui||'N/A'} · Tel: {p.telefono||'N/A'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Modo PARTICULAR: formulario rápido ── */}
        {modoParticular && !pacienteInfo && (
          <div style={{ background:'#f8f0fb', border:'2px solid #d8c8ea', borderRadius:'10px', padding:'16px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:'#6c3b9e', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'14px' }}>
              👤 Datos del paciente particular
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div style={S.campo}>
                <label style={{ ...S.label, color:'#6c3b9e' }}>Nombre *</label>
                <input style={S.input} value={partNombre}
                  onChange={e=>setPartNombre(e.target.value.toUpperCase())}
                  placeholder="PRIMER NOMBRE" />
              </div>
              <div style={S.campo}>
                <label style={{ ...S.label, color:'#6c3b9e' }}>Apellido *</label>
                <input style={S.input} value={partApellido}
                  onChange={e=>setPartApellido(e.target.value.toUpperCase())}
                  placeholder="PRIMER APELLIDO" />
              </div>
              <div style={S.campo}>
                <label style={S.label}>DUI (opcional)</label>
                <input style={S.input} value={partDui}
                  onChange={e=>setPartDui(e.target.value)}
                  placeholder="00000000-0" />
              </div>
              <div style={S.campo}>
                <label style={{ ...S.label, color:'#6c3b9e' }}>Teléfono *</label>
                <input style={S.input} value={partTelefono}
                  onChange={e=>setPartTelefono(e.target.value)}
                  placeholder="0000-0000" />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Fecha de nacimiento (opcional)</label>
                <input style={S.input} type="date" value={partFechaNac}
                  onChange={e=>setPartFechaNac(e.target.value)}
                  min="1900-01-01" max="9999-12-31" />
              </div>
            </div>
            <div style={{ background:'#fff3cd', borderRadius:'7px', padding:'8px 12px', fontSize:'11px', color:'#856404', marginBottom:'14px' }}>
              ℹ️ Si el DUI ya existe en el sistema, se vinculará al paciente registrado automáticamente.
              Se creará una admisión tipo <strong>"Exámenes"</strong> para gestionar el cobro.
            </div>
            <button
              style={{ ...S.btn('#6c3b9e'), width:'100%', padding:'11px', fontSize:'13px' }}
              onClick={registrarParticular} disabled={registrandoPart}>
              {registrandoPart ? '⏳ Registrando...' : '✓ Registrar y continuar →'}
            </button>
          </div>
        )}

        {/* ── Paciente seleccionado (ALTIMO o Particular) ── */}
        {pacienteInfo && (
          <div>
            <div style={S.pacienteBox}>
              <div>
                <div style={{ fontWeight:'700',color:'#1B2A5E',fontSize:'15px', display:'flex', alignItems:'center', gap:'8px' }}>
                  {pacienteInfo.nombre}
                  {admisionParticular && (
                    <span style={{ fontSize:'10px', fontWeight:'700', background:'#f4c0d1', color:'#72243E', padding:'2px 8px', borderRadius:'99px' }}>
                      PARTICULAR
                    </span>
                  )}
                </div>
                <div style={{ fontSize:'12px',color:'#6c757d',marginTop:'3px' }}>
                  DUI: {pacienteInfo.dui} · Admisión #{pacienteInfo.admision} · {pacienteInfo.tipo}
                  {' · '}<span style={{ fontWeight:'700', color:pacienteInfo.estado==='Activa'?'#155724':'#856404' }}>{pacienteInfo.estado}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:'8px' }}>
                {admisionParticular && (
                  <button style={{ ...S.btn('#6c3b9e'), fontSize:'11px', padding:'6px 12px' }}
                    onClick={generarReciboParticular}>
                    🧾 Recibo
                  </button>
                )}
                <button style={S.btnS} onClick={() => { admisionParticular ? limpiarParticular() : limpiarPaciente(); }}>
                  Cambiar paciente
                </button>
              </div>
            </div>
            {admisionParticular && (
              <div style={{ background:'#fff8e1', border:'1px solid #ffe082', borderRadius:'7px', padding:'8px 14px', marginTop:'8px', fontSize:'12px', color:'#795548', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>💡 Paciente particular — Admisión <strong>#{admisionParticular.id}</strong> creada. Puedes cobrar desde el botón <strong>Recibo</strong> o desde el módulo <strong>Facturación</strong>.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PASO 2: Tipo de examen y cargo */}
      {pacienteInfo && (
        <div style={S.card}>
          <div style={S.titulo}><span>Paso 2 — Tipo de examen y cargo</span></div>

          {/* Lab externo toggle */}
          <div style={S.seccionExterno}>
            <label style={{ display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'700',color:'#795548',marginBottom:esExterno?'14px':'0' }}>
              <input type="checkbox" checked={esExterno} onChange={e=>setEsExterno(e.target.checked)} style={{ width:'16px',height:'16px' }} />
              🔬 Examen de laboratorio EXTERNO (fuera de ALTIMO)
            </label>

            {esExterno && (
              <>
                <div style={S.fila2}>
                  {/* CAMPO LIBRE — nombre del examen para externos */}
                  <div style={S.campo}>
                    <label style={{ ...S.label, color:'#e65100' }}>Nombre / tipo de examen *</label>
                    <input
                      style={{ ...S.input, border:'2px solid #ff9800' }}
                      value={tipoExamenLibre}
                      onChange={e=>setTipoExamenLibre(e.target.value)}
                      placeholder="Ej: Hemograma, Glucosa, Rx Tórax, PCR..."
                      autoFocus
                    />
                    <div style={{ fontSize:'10px',color:'#795548',marginTop:'3px' }}>Escribe el nombre exacto del examen realizado</div>
                  </div>
                  <div style={S.campo}>
                    <label style={S.label}>Nombre del laboratorio externo</label>
                    <input style={S.input} value={labExterno} onChange={e=>setLabExterno(e.target.value)}
                      placeholder="Ej: Lab. San Rafael, LabCenter..." />
                  </div>
                </div>

                {/* Modo de cargo */}
                <div style={{ marginBottom:'10px' }}>
                  <label style={{ ...S.label,marginBottom:'8px' }}>¿Cómo cargar el monto a la cuenta?</label>
                  <div style={{ display:'flex',gap:'8px',flexWrap:'wrap' }}>
                    {[
                      { v:'tarifario',label:'📋 Usar tarifario',desc:'Buscar precio interno' },
                      { v:'manual',   label:'✏️ Monto manual',  desc:'Ingresar monto del lab externo' },
                      { v:'sin-cargo',label:'🚫 Sin cargo',      desc:'Solo guardar resultado' },
                    ].map(({ v,label,desc })=>(
                      <div key={v} onClick={()=>setModoCargoExterno(v)}
                        style={{ border:`2px solid ${modoCargoExterno===v?'#ff9800':'#dde3ea'}`, borderRadius:'8px', padding:'10px 14px', cursor:'pointer', flex:'1', minWidth:'140px', background:modoCargoExterno===v?'#fff8e1':'#fff' }}>
                        <div style={{ fontSize:'13px',fontWeight:'700',color:modoCargoExterno===v?'#e65100':'#495057',marginBottom:'2px' }}>{label}</div>
                        <div style={{ fontSize:'10px',color:'#6c757d' }}>{desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {modoCargoExterno==='manual' && (
                  <div style={S.montoManualBox}>
                    <div style={{ fontSize:'12px',fontWeight:'700',color:'#e65100',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:'10px' }}>
                      💵 Monto cobrado por el laboratorio externo
                    </div>
                    <div style={{ position:'relative',maxWidth:'220px' }}>
                      <span style={{ position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',color:'#1D9E75',fontWeight:'700',fontSize:'14px' }}>$</span>
                      <input type="number" min="0.01" step="0.01"
                        style={{ ...S.input,paddingLeft:'24px',fontSize:'15px',fontWeight:'700',color:'#1B2A5E',border:'2px solid #ff9800' }}
                        value={montoManual} onChange={e=>setMontoManual(e.target.value)} placeholder="0.00" />
                    </div>
                    {montoManual && parseFloat(montoManual)>0 && (
                      <div style={S.totalBadge}>💰 Cargo a cuenta: ${parseFloat(montoManual).toFixed(2)}</div>
                    )}
                  </div>
                )}

                {modoCargoExterno==='tarifario' && (
                  <div style={S.seccionTarifario}>
                    <label style={S.label}>Buscar en tarifario interno</label>
                    <div style={{ position:'relative' }}>
                      <input style={S.input} value={tarifaFiltro}
                        onChange={e=>{ setTarifaFiltro(e.target.value); setTarifaId(''); }}
                        placeholder="Nombre del examen..." />
                      {tarifaFiltro && !tarifaId && tarifasFiltradas.length>0 && (
                        <div style={S.resultado}>
                          {tarifasFiltradas.map(t=>(
                            <div key={t.id} style={S.resultadoItem}
                              onMouseEnter={e=>e.currentTarget.style.background='#f0f4f8'}
                              onMouseLeave={e=>e.currentTarget.style.background='#fff'}
                              onClick={()=>{ setTarifaId(String(t.id)); setTarifaFiltro(t.nombre); }}>
                              <strong>{t.nombre}</strong>
                              <span style={{ color:'#1D9E75',fontWeight:'700',marginLeft:'8px' }}>${Number(t.precio).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {tarifaId && tarifaSeleccionada && (
                      <div style={{ marginTop:'8px',padding:'8px 12px',background:'#d4edda',borderRadius:'7px',fontSize:'13px',color:'#155724',display:'flex',justifyContent:'space-between' }}>
                        <span>✓ {tarifaSeleccionada.nombre}</span>
                        <span style={{ fontWeight:'800' }}>${Number(tarifaSeleccionada.precio).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}

                {modoCargoExterno==='sin-cargo' && (
                  <div style={{ padding:'10px 14px',background:'#f8d7da',borderRadius:'8px',fontSize:'12px',color:'#721c24',fontWeight:'500' }}>
                    ⚠ El resultado se guardará SIN cargo a la cuenta del paciente.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Lab interno — select normal */}
          {!esExterno && (
            <>
              <div style={S.fila2}>
                <div style={S.campo}>
                  <label style={S.label}>Tipo de examen</label>
                  <select style={S.input} value={tipoExamen} onChange={e=>setTipoExamen(e.target.value)}>
                    {TIPOS_EXAMEN.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.campo}>
                  <label style={S.label}>Observaciones (opcional)</label>
                  <input style={S.input} value={notas} onChange={e=>setNotas(e.target.value)} placeholder="Valores de referencia, notas..." />
                </div>
              </div>
              <div style={{ ...S.seccionTarifario, background:cobrarCuenta?'#e8f0fb':'#f8fafd' }}>
                <label style={{ display:'flex',alignItems:'center',gap:'8px',cursor:'pointer',fontSize:'13px',fontWeight:'600',color:'#1B2A5E',marginBottom:cobrarCuenta?'12px':'0' }}>
                  <input type="checkbox" checked={cobrarCuenta} onChange={e=>setCobrarCuenta(e.target.checked)} />
                  Agregar cargo automático a la cuenta del paciente
                </label>
                {cobrarCuenta && (
                  <>
                    <label style={S.label}>Buscar en tarifario</label>
                    <div style={{ position:'relative' }}>
                      <input style={S.input} value={tarifaFiltro}
                        onChange={e=>{ setTarifaFiltro(e.target.value); setTarifaId(''); }}
                        placeholder="Nombre del examen en el tarifario..." />
                      {tarifaFiltro && !tarifaId && tarifasFiltradas.length>0 && (
                        <div style={S.resultado}>
                          {tarifasFiltradas.map(t=>(
                            <div key={t.id} style={S.resultadoItem}
                              onMouseEnter={e=>e.currentTarget.style.background='#f0f4f8'}
                              onMouseLeave={e=>e.currentTarget.style.background='#fff'}
                              onClick={()=>{ setTarifaId(String(t.id)); setTarifaFiltro(t.nombre); }}>
                              <strong>{t.nombre}</strong>
                              <span style={{ color:'#1D9E75',fontWeight:'700',marginLeft:'8px' }}>${Number(t.precio).toFixed(2)}</span>
                              <span style={{ color:'#adb5bd',fontSize:'11px',marginLeft:'6px' }}>{t.tipo}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {tarifaId && tarifaSeleccionada && (
                      <div style={{ marginTop:'8px',padding:'8px 12px',background:'#d4edda',borderRadius:'7px',fontSize:'13px',color:'#155724',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                        <span>✓ <strong>{tarifaSeleccionada.nombre}</strong></span>
                        <span style={{ fontWeight:'800',fontSize:'15px' }}>${Number(tarifaSeleccionada.precio).toFixed(2)}</span>
                      </div>
                    )}
                    {!tarifaId && <div style={{ fontSize:'11px',color:'#856404',marginTop:'6px' }}>⚠ Sin tarifa — se guardará sin cargo.</div>}
                  </>
                )}
              </div>
            </>
          )}

          {/* Notas para externo */}
          {esExterno && (
            <div style={S.campo}>
              <label style={S.label}>Observaciones adicionales</label>
              <input style={S.input} value={notas} onChange={e=>setNotas(e.target.value)} placeholder="Notas clínicas, valores de referencia..." />
            </div>
          )}
        </div>
      )}

      {/* PASO 3: Archivos */}
      {pacienteInfo && (
        <div style={S.card}>
          <div style={S.titulo}>
            <span>Paso 3 — Cargar archivo(s) del resultado</span>
            {montoACobrar && (
              <span style={{ fontSize:'13px',fontWeight:'700',color:'#1D9E75',background:'#d4edda',padding:'4px 12px',borderRadius:'6px' }}>
                💰 Cargo: ${montoACobrar.toFixed(2)}
              </span>
            )}
          </div>

          <div
            onDragOver={e=>{ e.preventDefault(); setDragOver(true); }}
            onDragLeave={()=>setDragOver(false)}
            onDrop={e=>{ e.preventDefault(); setDragOver(false); agregarAQueue([...e.dataTransfer.files]); }}
            onClick={()=>inputRef.current?.click()}
            style={{ border:`2px dashed ${dragOver?'#1B2A5E':'#c0ccda'}`,borderRadius:'10px',padding:'28px 20px',textAlign:'center',cursor:'pointer',background:dragOver?'#e8f0fb':'#f8fafd',transition:'all .15s',marginBottom:archivosQueue.length>0?'12px':'0' }}>
            <input ref={inputRef} type="file" multiple style={{ display:'none' }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tiff,.tif"
              onChange={e=>{ agregarAQueue([...e.target.files]); e.target.value=''; }} />
            <div style={{ fontSize:'32px',marginBottom:'6px' }}>📂</div>
            <div style={{ fontSize:'13px',fontWeight:'600',color:dragOver?'#1B2A5E':'#495057',marginBottom:'4px' }}>
              {dragOver?'Suelta aquí':'Arrastra archivos o haz clic para seleccionar'}
            </div>
            <div style={{ fontSize:'11px',color:'#adb5bd' }}>PDF, Word, JPG, PNG, TIFF · Máx. {MAX_MB} MB</div>
          </div>

          {archivosQueue.length>0 && (
            <>
              {archivosQueue.map((f,i)=>{
                const ext=f.name.split('.').pop().toLowerCase();
                return (
                  <div key={i} style={{ display:'flex',alignItems:'center',gap:'10px',padding:'8px 10px',background:'#f0f7ff',borderRadius:'7px',marginBottom:'6px',border:'1px solid #c8ddf7' }}>
                    <FileIcono ext={ext} />
                    <div style={{ flex:1,minWidth:0 }}>
                      <div style={{ fontSize:'13px',fontWeight:'600',color:'#1B2A5E',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{f.name}</div>
                      <div style={{ fontSize:'11px',color:'#6c757d' }}>{(f.size/1024/1024).toFixed(2)} MB</div>
                    </div>
                    <button style={{ background:'none',border:'none',cursor:'pointer',color:'#dc3545',fontSize:'16px',padding:'2px 6px' }}
                      onClick={()=>setArchivosQueue(q=>q.filter((_,j)=>j!==i))}>✕</button>
                  </div>
                );
              })}
              <button style={{ ...S.btn('#1D9E75'),width:'100%',padding:'11px',fontSize:'13px',marginTop:'6px' }}
                onClick={subirTodo} disabled={subiendo}>
                {subiendo ? 'Guardando...' : `Guardar ${archivosQueue.length} archivo(s)${montoACobrar?` · Cargo $${montoACobrar.toFixed(2)}`:' — sin cargo'}`}
              </button>
            </>
          )}
        </div>
      )}

      {/* Resultados */}
      {pacienteInfo && (
        <div style={S.card}>
          <div style={S.titulo}>
            <span>Resultados — Admisión #{admisionId} ({resultados.length})</span>
            <button style={S.btnS} onClick={cargarResultados}>Actualizar</button>
          </div>
          {cargando ? (
            <div style={{ textAlign:'center',padding:'28px',color:'#6c757d' }}>Cargando...</div>
          ) : resultados.length===0 ? (
            <div style={{ textAlign:'center',padding:'28px',color:'#adb5bd',fontSize:'13px' }}>No hay resultados cargados para esta admisión.</div>
          ) : (
            Object.entries(porTipo).map(([tipo,items])=>(
              <div key={tipo} style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'11px',fontWeight:'700',color:'#1B2A5E',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:'8px',padding:'4px 10px',background:'#e8f0fb',borderRadius:'5px',display:'inline-block' }}>
                  {tipo} ({items.length})
                </div>
                {items.map(r=>{
                  const ext=r.nombre_archivo?.split('.').pop().toLowerCase();
                  const externo=r.notas?.includes('Lab externo');
                  const montoExt=r.notas?.match(/Monto externo: \$([0-9.]+)/)?.[1];
                  return (
                    <div key={r.id} style={{ display:'flex',alignItems:'center',gap:'12px',padding:'10px 12px',border:'1px solid #eef1f6',borderRadius:'8px',marginBottom:'6px',background:'#fff' }}
                      onMouseEnter={e=>e.currentTarget.style.background='#f8fafd'}
                      onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                      <FileIcono ext={ext} />
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ display:'flex',alignItems:'center',gap:'6px',marginBottom:'2px' }}>
                          <span style={{ fontSize:'13px',fontWeight:'600',color:'#1B2A5E',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{r.nombre_archivo}</span>
                          {externo && <span style={{ fontSize:'10px',fontWeight:'700',background:'#fff3cd',color:'#856404',padding:'1px 6px',borderRadius:'4px',whiteSpace:'nowrap' }}>Lab externo</span>}
                          {montoExt && <span style={{ fontSize:'10px',fontWeight:'700',background:'#d4edda',color:'#155724',padding:'1px 6px',borderRadius:'4px',whiteSpace:'nowrap' }}>${montoExt}</span>}
                        </div>
                        <div style={{ fontSize:'11px',color:'#6c757d' }}>{new Date(r.fecha).toLocaleString('es-SV')}{r.notas && ` · ${r.notas}`}</div>
                      </div>
                      <div style={{ display:'flex',gap:'6px',flexShrink:0 }}>
                        <button style={S.btn('#1B2A5E')} onClick={()=>window.open(api.lab.urlArchivo(r.ruta_archivo), '_blank')}>Abrir</button>
                        <button style={S.btn('#dc3545')} onClick={async()=>{
                          if (!(await confirmar({
                            texto: `¿Eliminar "${r.nombre_archivo}"?`,
                            tipo: 'peligro',
                            textoConfirmar: 'Eliminar archivo',
                          }))) return;
                          const res=await api.lab.eliminar({ id:r.id });
                          if (res.ok) { mostrarMsg('ok','Eliminado.'); cargarResultados(); }
                          else mostrarMsg('err',res.error);
                        }}>Eliminar</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  PANEL PENDIENTES — bandeja de Laboratorio
//  Órdenes médicas esperando ser tomadas / completadas.
//  Tomar: solo marca En_Proceso (no cobra, no sube nada).
//  Completar: aquí se sube el archivo y SOLO entonces se cobra
//  a la cuenta del paciente (vía lab:subir con orden_id).
// ═════════════════════════════════════════════════════════════
const ESTADO_ORDEN_LAB = {
  Pendiente:  { color:'#fff3cd', texto:'#856404', label:'⏳ Pendiente' },
  En_Proceso: { color:'#cce5ff', texto:'#004085', label:'🧪 En proceso' },
};

function PanelOrdenesPendientes({ usuario, onActualizar, filtroInicial }) {
  const [ordenes,   setOrdenes]   = useState([]);
  const [cargando,  setCargando]  = useState(true);
  const [modal,     setModal]     = useState(null); // orden seleccionada para tomar/completar
  const [filtroEstado, setFiltroEstado] = useState(filtroInicial || 'Todos');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.ordenesLab.pendientes();
      setOrdenes(r?.ok ? r.data : []);
    } catch(_) { setOrdenes([]); }
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const tomarOrden = async (o) => {
    try {
      const r = await api.ordenesLab.tomar({ id: o.id, usuario_id: usuario.id });
      if (r?.ok) cargar();
      else alert(r?.error || 'No se pudo tomar la orden.');
    } catch(e) { alert(e.message); }
  };

  const ordenesFiltradas = ordenes.filter(o => filtroEstado === 'Todos' ? true : o.estado === filtroEstado);

  return (
    <div style={S.pagina}>
      <div style={S.card}>
        <div style={S.titulo}>
          <span>🔔 Órdenes de laboratorio pendientes ({ordenesFiltradas.length})</span>
          <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
            {['Todos','Pendiente','En_Proceso'].map(f => (
              <button key={f} onClick={() => setFiltroEstado(f)}
                style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:'600',
                  cursor:'pointer', border:'none',
                  background: filtroEstado === f ? '#0c8599' : '#e0f3f5',
                  color: filtroEstado === f ? '#fff' : '#0c8599' }}>
                {f === 'Todos' ? 'Todos' : f === 'Pendiente' ? 'Por tomar' : 'En proceso'}
              </button>
            ))}
            <button style={S.btnS} onClick={cargar}>Actualizar</button>
          </div>
        </div>

        {cargando ? (
          <div style={{ textAlign:'center', padding:'30px', color:'#6c757d' }}>⏳ Cargando...</div>
        ) : ordenesFiltradas.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px' }}>
            <div style={{ fontSize:'32px', marginBottom:'8px' }}>✅</div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'#1B2A5E' }}>Sin órdenes pendientes</div>
            <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'4px' }}>
              Todas las órdenes médicas activas ya fueron tomadas o completadas.
            </div>
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
            <thead>
              <tr>
                {['Fecha','Paciente','Hab.','Examen','Indicaciones','Médico','Estado',''].map(h => (
                  <th key={h} style={{ textAlign:'left', padding:'8px 10px', fontSize:'11px', color:'#6c757d',
                    fontWeight:'600', borderBottom:'2px solid #e8f0fb', background:'#f8fafd', whiteSpace:'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordenesFiltradas.map(o => {
                const est = ESTADO_ORDEN_LAB[o.estado] || { color:'#e2e3e5', texto:'#383d41', label:o.estado };
                return (
                  <tr key={o.id}>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', fontSize:'11px', color:'#6c757d', whiteSpace:'nowrap' }}>
                      {new Date(o.creado_en).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' })}
                    </td>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', fontWeight:700, color:'#1B2A5E' }}>{o.paciente}</td>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>{o.habitacion || '—'}</td>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>{o.tipo_examen}</td>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', fontSize:'11px', color:'#6c757d', maxWidth:'180px' }}>
                      {o.indicaciones || '—'}
                    </td>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', fontSize:'11px' }}>
                      {o.medico_nombre ? `${o.medico_nombre} ${o.medico_apellido||''}` : '—'}
                    </td>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6' }}>
                      <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:'99px', fontSize:'10px',
                        fontWeight:'700', background: est.color, color: est.texto }}>
                        {est.label}
                      </span>
                    </td>
                    <td style={{ padding:'9px 10px', borderBottom:'1px solid #eef1f6', whiteSpace:'nowrap' }}>
                      {o.estado === 'Pendiente' && (
                        <button style={S.btn('#0c8599')} onClick={() => tomarOrden(o)}>Tomar orden</button>
                      )}
                      {o.estado === 'En_Proceso' && (
                        <button style={S.btn('#1D9E75')} onClick={() => setModal(o)}>📤 Subir resultado</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ModalSubirResultadoOrden
          orden={modal}
          usuario={usuario}
          onCerrar={() => setModal(null)}
          onListo={() => { setModal(null); cargar(); onActualizar(); }}
        />
      )}
    </div>
  );
}

const INTERPRETACIONES = [
  { value:'Normal',     label:'✅ Normal',     desc:'Todos los parámetros dentro del rango de referencia.' },
  { value:'Alterado',   label:'⚠️ Alterado',   desc:'Uno o más parámetros fuera del rango, pero ninguno crítico.' },
  { value:'Critico',    label:'🚨 Crítico',    desc:'Existe al menos un parámetro con valor crítico definido por el laboratorio.' },
  { value:'Inconcluso', label:'❓ Inconcluso', desc:'Hay parámetros pendientes o no procesados.' },
  { value:'Invalido',   label:'🚫 Inválido',   desc:'La muestra presenta problemas de calidad (hemólisis, lipemia, coagulación, volumen insuficiente, etc.).' },
];

// ── Modal: Laboratorio sube el resultado de una orden ya tomada ──
// Aquí — y solo aquí — se cobra el examen a la cuenta del paciente.
function ModalSubirResultadoOrden({ orden, usuario, onCerrar, onListo }) {
  const [archivo,       setArchivo]       = useState(null);
  const [notas,         setNotas]         = useState('');
  const [interpretacion,setInterpretacion]= useState('');
  const [subiendo,      setSubiendo]      = useState(false);
  const [err,           setErr]           = useState('');
  const inputRef = useRef();

  const leerBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror  = reject;
    reader.readAsDataURL(file);
  });

  const confirmar = async () => {
    setErr('');
    if (!archivo) { setErr('Selecciona el archivo de resultado.'); return; }
    const ext = '.' + archivo.name.split('.').pop().toLowerCase();
    if (!EXTS_OK.includes(ext.slice(1))) { setErr('Formato no permitido. Usa PDF, Word o imagen.'); return; }
    if (archivo.size > MAX_MB * 1024 * 1024) { setErr(`El archivo excede ${MAX_MB}MB.`); return; }
    if (!interpretacion) { setErr('Selecciona la interpretación clínica del resultado.'); return; }

    setSubiendo(true);
    try {
      const fileData = await leerBase64(archivo);
      const r = await api.lab.subir({
        admision_id: orden.admision_id, paciente_id: orden.paciente_id,
        tipo_examen: orden.tipo_examen, notas: notas.trim() || null,
        interpretacion,
        usuario_id: usuario.id, fileData, fileExt: ext, orden_id: orden.id,
      });
      if (r?.ok) onListo();
      else setErr(r?.error || 'No se pudo subir el resultado.');
    } catch(e) { setErr(e.message); }
    setSubiendo(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(20,20,30,.7)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9500 }}
      onClick={onCerrar}>
      <div style={{ background:'#fff', borderRadius:'12px', width:'460px', overflow:'hidden' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ background:'#1D9E75', padding:'16px 20px', color:'#fff' }}>
          <div style={{ fontSize:'15px', fontWeight:800 }}>📤 Subir resultado</div>
          <div style={{ fontSize:'11px', color:'rgba(255,255,255,.85)', marginTop:'2px' }}>
            {orden.paciente} {orden.habitacion ? `· Hab. ${orden.habitacion}` : ''}
          </div>
        </div>
        <div style={{ padding:'20px' }}>
          <div style={{ marginBottom:'14px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'#1B2A5E' }}>{orden.tipo_examen}</div>
            {orden.indicaciones && (
              <div style={{ fontSize:'11px', color:'#6c757d', marginTop:'2px', fontStyle:'italic' }}>
                "{orden.indicaciones}"
              </div>
            )}
            {orden.tarifa_precio && (
              <div style={{ fontSize:'11px', color:'#1D9E75', marginTop:'4px', fontWeight:700 }}>
                Se cobrará ${Number(orden.tarifa_precio).toFixed(2)} a la cuenta al confirmar.
              </div>
            )}
            {!orden.tarifa_precio && (
              <div style={{ fontSize:'11px', color:'#e67e22', marginTop:'4px', fontWeight:700 }}>
                ⚠ Esta orden no tiene tarifa asociada — no generará cargo automático.
              </div>
            )}
          </div>

          <label style={S.label}>Archivo de resultado (PDF, Word o imagen) *</label>
          <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.tiff,.tif"
            onChange={e => setArchivo(e.target.files?.[0] || null)}
            style={{ width:'100%', fontSize:'12px', marginTop:'4px' }} />

          <div style={{ marginTop:'12px' }}>
            <label style={S.label}>Interpretación clínica del resultado *</label>
            <div style={{ display:'flex', flexDirection:'column', gap:'5px', marginTop:'4px' }}>
              {INTERPRETACIONES.map(opt => (
                <label key={opt.value}
                  style={{
                    display:'flex', alignItems:'flex-start', gap:'8px', padding:'7px 10px',
                    border:`1px solid ${interpretacion===opt.value ? '#1D9E75' : '#dde3ea'}`,
                    background: interpretacion===opt.value ? '#eafaf3' : '#fff',
                    borderRadius:'7px', cursor:'pointer', fontSize:'12px',
                  }}>
                  <input type="radio" name="interpretacion" value={opt.value}
                    checked={interpretacion===opt.value}
                    onChange={() => setInterpretacion(opt.value)}
                    style={{ marginTop:'2px' }} />
                  <span>
                    <strong>{opt.label}</strong>
                    <div style={{ color:'#6c757d', fontSize:'11px' }}>{opt.desc}</div>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginTop:'12px' }}>
            <label style={S.label}>Notas (opcional)</label>
            <input style={S.input} placeholder="Observaciones sobre el resultado..."
              value={notas} onChange={e => setNotas(e.target.value)} />
          </div>

          {err && <div style={{ color:'#dc3545', fontSize:'12px', fontWeight:600, marginTop:'8px' }}>⚠ {err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
            <button style={S.btnS} onClick={onCerrar}>Cancelar</button>
            <button style={{ ...S.btn('#1D9E75'), flex:1, opacity: subiendo ? .6 : 1 }}
              disabled={subiendo} onClick={confirmar}>
              {subiendo ? 'Subiendo...' : '✔ Confirmar y cargar al expediente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
//  COMPONENTE EXPORTADO — envuelve con tabs
// ═════════════════════════════════════════════════════════════
const TABS_LAB = [
  { id:'registro',    label:'📋 Registrar examen' },
  { id:'pendientes',  label:'🔔 Pendientes' },
];

export default function Laboratorio({ usuario, filtroInicial }) {
  const [tab, setTab] = useState(filtroInicial ? 'pendientes' : 'registro');

  return (
    <div style={{ fontFamily:'Segoe UI, sans-serif' }}>
      <div style={{ background:'linear-gradient(135deg,#0c8599,#0b5563)', padding:'16px 24px 0' }}>
        <div style={{ fontSize:'19px', fontWeight:'800', color:'#fff', marginBottom:'3px' }}>
          🧪 Módulo de Laboratorio
        </div>
        <div style={{ fontSize:'12px', color:'rgba(255,255,255,.8)', marginBottom:'12px' }}>
          Registro de exámenes · Órdenes médicas · Resultados clínicos
        </div>
        <div style={{ display:'flex', gap:'2px' }}>
          {TABS_LAB.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:'9px 20px', border:'none', cursor:'pointer', fontSize:'13px', fontWeight:'700',
                borderRadius:'8px 8px 0 0', fontFamily:'inherit',
                background: tab === t.id ? '#fff' : 'rgba(255,255,255,.15)',
                color:      tab === t.id ? '#0b5563' : '#fff' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'registro'   && <RegistroExamenes usuario={usuario} />}
      {tab === 'pendientes' && <PanelOrdenesPendientes usuario={usuario} onActualizar={() => {}} filtroInicial={filtroInicial} />}
    </div>
  );
}
